import { FC, useContext } from "react";
import { Anchor, Badge, Box, Flex, Text } from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import { Link } from "react-router-dom";
import { TeamLogo } from "../../../../Components/TeamLogo";
import { TeamName } from "../../../../Components/TeamName";
import { CompetitionContext } from "../../../../CompetitionContext";
import { useGameList } from "../../../../hooks/useGameList";
import { useCompetition } from "../../../../hooks/useCompetition";
import { IconChevronRight } from "@tabler/icons-react";
import { CenteredLoader } from "../../../../Components/CenteredLoader";
import { components } from "../../../../openapi/generated-types";
import { CardContainer } from "../../../../Components/CardContainer";
import { WidgetHeader } from "../../../../Components/WidgetHeader";

import "@mantine/carousel/styles.css";

interface ResultCardProps {
  game: components["schemas"]["Game"];
  matchdayName?: string;
}

const ResultCard: FC<ResultCardProps> = ({ game, matchdayName }) => {
  const { homeTeamId, guestTeamId } = game.actualTeams;
  if (!homeTeamId || !guestTeamId) return null;

  const result = game.result;
  const isHomeWin =
    result && "winnerTeamId" in result && result.winnerTeamId === homeTeamId;
  const isGuestWin =
    result && "winnerTeamId" in result && result.winnerTeamId === guestTeamId;

  return (
    <CardContainer p="sm" h="100%">
      {/* Header with matchday */}
      <Flex justify="space-between" align="center" mb="xs">
        <Text size="xs" c="dimmed" fw={500}>
          {matchdayName ?? game.name}
        </Text>
      </Flex>

      {/* Teams and Score */}
      <Flex align="center" gap="xs">
        {/* Home Team */}
        <Anchor
          component={Link}
          to={`/teams/${homeTeamId}`}
          underline="never"
          c="inherit"
          style={{ flex: 1 }}
        >
          <Flex direction="column" align="center" gap={4}>
            <Box style={{ opacity: isGuestWin ? 0.5 : 1 }}>
              <TeamLogo teamId={homeTeamId} height={36} />
            </Box>
            <Text
              size="xs"
              fw={isHomeWin ? 600 : 400}
              ta="center"
              lineClamp={1}
              c={isGuestWin ? "dimmed" : undefined}
            >
              <TeamName teamId={homeTeamId} short />
            </Text>
          </Flex>
        </Anchor>

        {/* Score */}
        <Flex direction="column" align="center" gap={2} px="xs">
          <Text
            fw={700}
            style={{
              fontSize: 24,
              fontFamily: "Chivo Mono",
              lineHeight: 1,
            }}
          >
            {game.score.home}:{game.score.guest}
          </Text>
          {game.shootOutStarted && (
            <Badge size="xs" color="gray" variant="light">
              n.9m
            </Badge>
          )}
        </Flex>

        {/* Guest Team */}
        <Anchor
          component={Link}
          to={`/teams/${guestTeamId}`}
          underline="never"
          c="inherit"
          style={{ flex: 1 }}
        >
          <Flex direction="column" align="center" gap={4}>
            <Box style={{ opacity: isHomeWin ? 0.5 : 1 }}>
              <TeamLogo teamId={guestTeamId} height={36} />
            </Box>
            <Text
              size="xs"
              fw={isGuestWin ? 600 : 400}
              ta="center"
              lineClamp={1}
              c={isHomeWin ? "dimmed" : undefined}
            >
              <TeamName teamId={guestTeamId} short />
            </Text>
          </Flex>
        </Anchor>
      </Flex>
    </CardContainer>
  );
};

export const ResultCards: FC<{ maxGames?: number }> = ({ maxGames = 8 }) => {
  const competitionId = useContext(CompetitionContext);

  const games = useGameList(competitionId);
  const competition = useCompetition(competitionId);

  if (!games.isSuccess) {
    return <CenteredLoader />;
  }

  const lastGames = games.data.games
    .filter((g) => g.result)
    .slice(-maxGames)
    .reverse();

  if (lastGames.length === 0) {
    return null;
  }

  // Create a map of matchday IDs to names
  const matchdayMap = new Map<string, string>();
  if (competition.isSuccess) {
    for (const matchday of competition.data.matchdays) {
      matchdayMap.set(matchday.id, matchday.name);
    }
  }

  return (
    <Box>
      <WidgetHeader
        title="Ergebnisse"
        order={3}
        linkText="Alle Spiele"
        linkTo="/games"
      />
      <Carousel
        slideSize={{ base: "50%", sm: "33.333%", md: "25%" }}
        slideGap="sm"
        withControls={false}
      >
        {lastGames.map((game) => (
          <Carousel.Slide key={game.id}>
            <ResultCard
              game={game}
              matchdayName={
                game.matchdayId ? matchdayMap.get(game.matchdayId) : undefined
              }
            />
          </Carousel.Slide>
        ))}
        <Carousel.Slide>
          <Anchor component={Link} to="/games" underline="never">
            <CardContainer p="sm" h="100%">
              <Flex
                direction="column"
                align="center"
                justify="center"
                h="100%"
                gap="xs"
              >
                <IconChevronRight
                  size={24}
                  color="var(--mantine-color-dimmed)"
                />
                <Text
                  size="sm"
                  c="dimmed"
                  fw={500}
                  style={{ whiteSpace: "nowrap" }}
                >
                  Alle Spiele
                </Text>
              </Flex>
            </CardContainer>
          </Anchor>
        </Carousel.Slide>
      </Carousel>
    </Box>
  );
};
